import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Task } from '../../interface/task.interface';
import { TASKS_REDUCER_KEY } from '../reducers';
import { getSessionUserCurrentOrganizationRoleIds$ } from './roles.selector';
import { getCurrentOrganizationId$, getSessionUserId$ } from './session.selector';

import type { TasksState } from '../states';
const currentState = createFeatureSelector<TasksState>(TASKS_REDUCER_KEY);

export const getTaskDefinitionId$ = createSelector(currentState, state => state.taskDefinitionId);

export const getSelectedTaskType$ = createSelector(currentState, state => state.selectedTaskType);

export const getSelectedTaskId$ = createSelector(currentState, state => state.selectedTaskId);

export const getSelectedParentInstanceId$ = createSelector(currentState, state => state.selectedRootInstanceId);

export const getTasksOrganizationMap$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state?.organizationsMap?.[organizationId]
);

export const getTaskUserMetaprincipalIds$ = createSelector(
    getSessionUserCurrentOrganizationRoleIds$,
    getSessionUserId$,
    (roleIds: string[], userId: string) => (roleIds?.length > 0 ? [...roleIds, userId] : [])
);

export const getTasks$ = createSelector(getTasksOrganizationMap$, orgMap => {
    return orgMap?.tasks || [];
});

export const getTask$ = createSelector(getSelectedTaskId$, getTasksOrganizationMap$, (taskId, orgMap) =>
    getTaskRecursively(orgMap?.tasks || [], taskId)
);

export const getTaskByDefinitionId$ = (taskId: string) =>
    createSelector(getTasksOrganizationMap$, orgMap => getTaskRecursively(orgMap?.tasks || [], taskId));

export const getTaskRecursively = (tasks: Task[], taskId: string): Task => {
    for (const task of tasks) {
        if (task.id === taskId) {
            return task;
        }
        if (task.childTasks?.length > 0) {
            const foundTask = getTaskRecursively(task.childTasks, taskId);
            if (foundTask) {
                return foundTask;
            }
        }
    }
    return undefined;
};

export const getTaskAssignees$ = createSelector(
    getSelectedTaskId$,
    getTasksOrganizationMap$,
    (taskId, orgMap) => orgMap?.taskAssignees?.[taskId]
);

export const getTaskAssigneesByTaskType$ = createSelector(
    getSelectedTaskId$,
    getSelectedTaskType$,
    getSelectedParentInstanceId$,
    getTasksOrganizationMap$,
    (taskId, taskType, instanceId, orgMap) => {
        switch (taskType) {
            case 'TASK_DEFINITION':
                return orgMap?.taskAssignees?.[taskId];
            case 'TASK_INSTANCE':
                return orgMap?.taskAssignees?.[instanceId];
        }
    }
);

export const getTaskInstances$ = createSelector(getTasksOrganizationMap$, orgMap => orgMap?.taskInstances || {});

export const getFilteredTaskInstanceIds$ = createSelector(
    getTasksOrganizationMap$,
    getSelectedParentInstanceId$,
    (orgMap, rootInstanceId) => {
        return orgMap?.filteredChildTaskInstanceIds?.[rootInstanceId];
    }
);

export const getSelectedTaskInstances$ = createSelector(getTaskInstances$, getTaskDefinitionId$, (taskInstances, taskDefId) => {
    const instances = taskInstances?.[taskDefId] || [];
    return instances;
});

export const getFilteredSelectedTaskInstances$ = createSelector(
    getSelectedTaskInstances$,
    getFilteredTaskInstanceIds$,
    getSelectedParentInstanceId$,
    (instances, filteredIds, rootInstanceId) => {
        // If filter is defined, return only the filtered task instances with their hierarchy
        if (filteredIds && Array.isArray(filteredIds) && filteredIds.length > 0) {
            const filteredIdSet = new Set(filteredIds);

            // Recursively filter child tasks, keeping parent if any descendant matches
            const filterTaskHierarchy = (task: Task): Task | null => {
                // Check if current task matches filter
                const matches = filteredIdSet.has(task.id);

                // Recursively filter children
                let filteredChildren: Task[] = [];
                if (task.childTasks && task.childTasks.length > 0) {
                    filteredChildren = task.childTasks
                        .map(child => filterTaskHierarchy(child))
                        .filter((child): child is Task => child !== null);
                }

                // Include task if it matches OR has matching children
                if (matches || filteredChildren.length > 0) {
                    return {
                        ...task,
                        childTasks: filteredChildren,
                    };
                }

                return null;
            };

            // Apply filtering to root-level instances
            const filteredInstances =
                instances
                    .map(task => filterTaskHierarchy(task))
                    .filter((task): task is Task => task !== null)
                    ?.find(i => i.id === rootInstanceId)?.childTasks || [];
            return { allInstances: instances, filteredInstances: filteredInstances };
        }
        return { allInstances: instances, filteredInstances: [] };
    }
);

export const getSelectedRootTaskInstance$ = createSelector(
    getSelectedTaskInstances$,
    getSelectedParentInstanceId$,
    (taskInstances, instanceId) => taskInstances?.find(task => task.id === instanceId)
);

export const getTaskInstance$ = createSelector(getSelectedTaskInstances$, getSelectedTaskId$, (taskInstances, taskId) =>
    getChildTaskInstance(taskInstances, taskId)
);

export const getChildTaskInstance = (taskInstances: any[] | [], taskId: string): any => {
    for (const task of taskInstances) {
        if (task.id === taskId) {
            return task;
        }
        if (task.childTasks?.length) {
            const found = task.childTasks.length > 0 && getChildTaskInstance(task.childTasks, taskId);
            if (found) return found;
        }
    }
    return;
};

export const getTaskBySelectedType$ = createSelector(getSelectedTaskType$, getTask$, getTaskInstance$, (type, task, instance) => {
    switch (type) {
        case 'TASK_DEFINITION':
            return task;
        case 'TASK_INSTANCE':
            return instance;
    }
});

export const getChildrenRecursively = (tasks: Task[], taskId: string): Task[] => {
    for (const task of tasks) {
        // Check if the current task matches the taskId
        if (task.id === taskId) {
            return task.childTasks;
        }
        // Recursively search in the children
        if (task.childTasks?.length > 0) {
            const childTasks = getChildrenRecursively(task.childTasks, taskId);
            if (childTasks?.length > 0) {
                return childTasks;
            }
        }
    }
    return []; // Return an empty array if no task is found with the given taskId
};

export const getSelectedTaskLinks$ = createSelector(getTaskBySelectedType$, (task: Task) => {
    return task?.links || [];
});

export const getSelectedTaskNotifiers$ = createSelector(getTaskBySelectedType$, (task: Task) => {
    return task?.notifiers || [];
});

export const getChildTasks$ = (taskId: string) =>
    createSelector(getTasksOrganizationMap$, (taskMap: any) => getChildrenRecursively(taskMap?.tasks || [], taskId));

const taskInstancesMap$ = createSelector(currentState, state => state.futureTaskSchedules);

export const getFutureTaskSchedules$ = createSelector(taskInstancesMap$, taskInstances => taskInstances);

export const getRootChildTasks$ = createSelector(getTasksOrganizationMap$, getTaskDefinitionId$, (taskMap: any, taskId: string) =>
    getChildrenRecursively(taskMap?.tasks || [], taskId)
);

export const getRootChildTaskDefinitionId$ = createSelector(
    getTasksOrganizationMap$,
    getTaskDefinitionId$,
    (taskMap: any, taskId: string) => getChildrenRecursively(taskMap?.tasks || [], taskId)?.[0]?.task_definition_id
);

export const getComments$ = createSelector(
    getTasksOrganizationMap$,
    getSelectedTaskId$,
    (taskMap: any, taskId: string) => taskMap?.comments?.[taskId] || []
);

export const getActionUpdates$ = createSelector(
    getTasksOrganizationMap$,
    getSelectedTaskId$,
    (taskMap: any, taskId: string) => taskMap?.actionUpdates?.[taskId] || []
);

export const getChildPlanWithParentTaskDefinitionId$ = (taskId: string) =>
    createSelector(getTasksOrganizationMap$, (taskMap: any) => getChildPlanWithParentTaskDefinitionId(taskMap?.tasks || [], taskId));

const getChildPlanWithParentTaskDefinitionId = (tasks: Task[], taskId: string): Task[] => {
    for (const task of tasks) {
        if (task.parent === taskId) {
            return tasks?.filter(t => t.parent === taskId);
        }
        if (task.childTasks?.length > 0) {
            const childTasks = getChildPlanWithParentTaskDefinitionId(task.childTasks, taskId);
            if (childTasks?.length > 0) {
                return childTasks;
            }
        }
    }
    return [];
};

export const getStatusGroups$ = createSelector(currentState, state => state.statusGroups || []);

export const getTaskActionsMap$ = createSelector(getTasksOrganizationMap$, tasksOrgMap => {
    return tasksOrgMap?.taskActions;
});

export const getTaskActions$ = createSelector(getSelectedTaskId$, getTaskActionsMap$, (taskDefId, taskActionsMap) => {
    return taskActionsMap?.[taskDefId] || [];
});
